import React, { useState, useEffect, useCallback } from 'react';
import { useSchedule, generateRecurringDates } from '../hooks/useSchedule';
import { useCasts } from '../hooks/useFirebase';

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];
const NTH_OPTIONS = [
  { value: 1, label: '第1' },
  { value: 2, label: '第2' },
  { value: 3, label: '第3' },
  { value: 4, label: '第4' },
];

const parseDate = (str) => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const fmtDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const todayStr = () => fmtDate(new Date());

// ── カレンダーグリッド ─────────────────────────────────────────────────────────
const CalendarMonth = ({ year, month, dates, attendance, casts, onToggleDate, onSelectDate, selectedDate, sessions, session_attendance }) => {
  const firstDay   = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = todayStr();
  const mm = String(month + 1).padStart(2, '0');

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const countWorking = useCallback((dateStr) => {
    if (sessions) {
      return Object.keys(casts).filter(cid =>
        Object.values(session_attendance?.[cid]?.[dateStr] || {}).some(s => s === 'working')
      ).length;
    }
    return Object.keys(casts).filter(cid => attendance[cid]?.[dateStr] === 'working').length;
  }, [casts, attendance, sessions, session_attendance]);

  return (
    <div style={{ flex: 1, minWidth: 260 }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 12, textAlign: 'center' }}>
        {year}年{month + 1}月
      </div>

      {/* 曜日ヘッダー */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 6 }}>
        {WEEKDAY_LABELS.map((w, i) => (
          <div key={i} style={{
            textAlign: 'center', fontSize: 11, fontWeight: 700,
            color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : '#94a3b8',
            padding: '3px 0',
          }}>{w}</div>
        ))}
      </div>

      {/* 日付グリッド */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`e${idx}`} />;
          const dateStr = `${year}-${mm}-${String(day).padStart(2, '0')}`;
          const isEvent = !!dates[dateStr];
          const isToday = dateStr === today;
          const isSel   = dateStr === selectedDate;
          const wc      = countWorking(dateStr);
          const wd      = (firstDay + day - 1) % 7;

          // 開催日=紫塗り / 選択中=白地に太ボーダー（開催日と被らない配色）
          const bg = isEvent ? (isSel ? '#4338ca' : '#6c63ff')
            : isToday ? '#f1f5f9'
            : 'transparent';

          const ring = isSel && !isEvent ? '2.5px solid #6c63ff' : 'none';

          const textColor = isEvent ? '#fff'
            : isSel ? '#6c63ff'
            : wd === 0 ? '#ef4444' : wd === 6 ? '#3b82f6' : '#475569';

          return (
            <div
              key={dateStr}
              onClick={() => onSelectDate(dateStr)}
              onDoubleClick={() => onToggleDate(dateStr)}
              title={isEvent ? 'ダブルクリックで開催日を解除' : 'ダブルクリックで開催日に設定'}
              style={{
                aspectRatio: '1',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 1,
                borderRadius: 9, cursor: 'pointer',
                background: bg,
                outline: isSel && !isEvent ? ring : isToday && !isEvent ? '2px solid #6c63ff33' : 'none',
                transition: 'all 0.12s', userSelect: 'none',
              }}
            >
              <span style={{
                fontSize: 13, fontWeight: isEvent || isToday ? 800 : 500,
                color: textColor, lineHeight: 1,
              }}>{day}</span>
              {isEvent && wc > 0 && (
                <span style={{ fontSize: 9, fontWeight: 800, lineHeight: 1, color: isSel ? '#a5f3c4' : '#bbf7d0' }}>
                  +{wc}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── 繰り返し設定パネル ────────────────────────────────────────────────────────
const RecurrencePanel = ({ onApply }) => {
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);
  const [selectedNth, setSelectedNth] = useState([]);

  const toggleWeekday = (wd) =>
    setSelectedWeekdays(prev => prev.includes(wd) ? prev.filter(v => v !== wd) : [...prev, wd]);

  const toggleNth = (n) =>
    setSelectedNth(prev => prev.includes(n) ? prev.filter(v => v !== n) : [...prev, n]);

  const handleApply = () => {
    if (selectedWeekdays.length === 0) return;
    onApply({ weekdays: selectedWeekdays, nth: selectedNth.length > 0 ? selectedNth : null });
  };

  return (
    <div style={{ background: '#f8f9fd', border: '1px solid #e8edf5', borderRadius: 12, padding: '14px 16px' }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', marginBottom: 10 }}>
        繰り返し設定（今月・来月に一括適用）
      </div>

      {/* 曜日選択 */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, marginBottom: 6 }}>曜日（複数選択可）</div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {WEEKDAY_LABELS.map((w, wd) => {
            const active = selectedWeekdays.includes(wd);
            return (
              <button key={wd} onClick={() => toggleWeekday(wd)} style={{
                padding: '5px 10px', borderRadius: 8, border: 'none',
                background: active ? '#6c63ff' : '#fff',
                color: active ? '#fff' : wd === 0 ? '#ef4444' : wd === 6 ? '#3b82f6' : '#475569',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)', transition: 'all 0.15s',
              }}>{w}</button>
            );
          })}
        </div>
      </div>

      {/* 週フィルター */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, marginBottom: 6 }}>
          週（未選択=毎週 / 複数選択可）
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {NTH_OPTIONS.map(({ value, label }) => {
            const active = selectedNth.includes(value);
            return (
              <button key={value} onClick={() => toggleNth(value)} style={{
                padding: '5px 10px', borderRadius: 8,
                border: active ? '1px solid #86efac' : '1px solid transparent',
                background: active ? '#f0fdf4' : '#fff',
                color: active ? '#16a34a' : '#475569',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)', transition: 'all 0.15s',
              }}>{label}</button>
            );
          })}
          {selectedNth.length > 0 && (
            <button onClick={() => setSelectedNth([])} style={{
              padding: '5px 10px', borderRadius: 8,
              border: '1px solid #e2e8f0', background: 'transparent',
              color: '#94a3b8', fontSize: 11, cursor: 'pointer',
            }}>毎週に戻す</button>
          )}
        </div>
      </div>

      <button
        onClick={handleApply}
        disabled={selectedWeekdays.length === 0}
        style={{
          width: '100%', padding: '10px', borderRadius: 10, border: 'none',
          background: selectedWeekdays.length > 0 ? '#6c63ff' : '#e2e8f0',
          color: selectedWeekdays.length > 0 ? '#fff' : '#94a3b8',
          fontWeight: 800, fontSize: 13,
          cursor: selectedWeekdays.length > 0 ? 'pointer' : 'not-allowed',
        }}
      >
        今月・来月に適用する
      </button>
    </div>
  );
};

// ── 日付詳細パネル ─────────────────────────────────────────────────────────────
// ── 出欠リストの1行（コメントありはクリックで全文展開）─────────────────────
const CastRow = ({ name, comment, bg, fontSize = 13 }) => {
  const [open, setOpen] = useState(false);
  const hasComment = !!comment;

  return (
    <div style={{ background: bg, borderRadius: 8, marginBottom: 3, overflow: 'hidden' }}>
      <div
        onClick={() => hasComment && setOpen(o => !o)}
        style={{
          padding: '6px 10px', fontSize, fontWeight: 600, color: '#1e293b',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          cursor: hasComment ? 'pointer' : 'default',
        }}
      >
        <span>{name}</span>
        {hasComment && (
          <span style={{ fontSize: 11, color: '#6c63ff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
            💬<span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none', fontSize: 9 }}>▼</span>
          </span>
        )}
      </div>
      {hasComment && open && (
        <div style={{
          padding: '8px 10px 10px', fontSize: 12, color: '#475569', lineHeight: 1.6,
          background: 'rgba(255,255,255,0.6)', borderTop: '1px solid rgba(0,0,0,0.05)',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {comment}
        </div>
      )}
    </div>
  );
};

const DayDetail = ({ dateStr, dates, attendance, casts, sessions, session_attendance, cast_comments }) => {
  if (!dateStr) return (
    <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
      <div style={{ fontSize: 13 }}>日付をクリックして詳細を確認</div>
    </div>
  );

  const d = parseDate(dateStr);
  const label = `${d.getMonth() + 1}月${d.getDate()}日（${WEEKDAY_LABELS[d.getDay()]}）`;
  const isEvent = !!dates[dateStr];

  const castEntries = Object.entries(casts).map(([castId, cast]) => ({
    castId,
    name: cast.vrchat_name || cast.name || '(名前なし)',
    status: attendance[castId]?.[dateStr] ?? null,
    comment: cast_comments?.[castId]?.[dateStr] ?? null,
  }));

  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 8 }}>{label}</div>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 12px', borderRadius: 20, marginBottom: 16,
        background: isEvent ? '#f0efff' : '#f1f5f9',
        color: isEvent ? '#6c63ff' : '#94a3b8',
        fontSize: 12, fontWeight: 700,
      }}>
        {isEvent ? '📅 開催日' : '非開催日'}
      </div>

      {castEntries.length === 0 ? (
        <p style={{ fontSize: 13, color: '#94a3b8' }}>キャストが登録されていません</p>
      ) : sessions ? (
        /* 部制あり: 部ごとに出欠一覧 */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {Array.from({ length: sessions }, (_, i) => i + 1).map(ses => {
            const sesEntries = castEntries.map(c => ({
              ...c,
              sesStatus: session_attendance?.[c.castId]?.[dateStr]?.[ses] ?? null,
            }));
            const working = sesEntries.filter(c => c.sesStatus === 'working');
            const absent  = sesEntries.filter(c => c.sesStatus === 'absent');
            const unset   = sesEntries.filter(c => c.sesStatus === null);
            return (
              <div key={ses}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#6c63ff', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ background: '#f0efff', borderRadius: 6, padding: '2px 8px' }}>第{ses}部</span>
                  <span style={{ color: '#10b981', fontWeight: 700 }}>✓ 出勤 {working.length}</span>
                  <span style={{ color: '#ef4444', fontWeight: 700 }}>✕ 欠勤 {absent.length}</span>
                  <span style={{ color: '#94a3b8', fontWeight: 600 }}>? 未 {unset.length}</span>
                </div>
                {[
                  { list: working, color: '#10b981', bg: '#ecfdf5' },
                  { list: absent,  color: '#ef4444', bg: '#fef2f2' },
                  { list: unset,   color: '#94a3b8', bg: '#f8f9fd' },
                ].map(({ list, color, bg }) => list.map(c => (
                  <CastRow key={c.castId} name={c.name} comment={c.comment} bg={bg} fontSize={12} />
                )))}
              </div>
            );
          })}
        </div>
      ) : (
        /* 部制なし: 従来表示 */
        <>
          {[
            { list: castEntries.filter(c => c.status === 'working'), label: '出勤予定', color: '#10b981', bg: '#ecfdf5', icon: '✓' },
            { list: castEntries.filter(c => c.status === 'absent'),  label: '欠勤',     color: '#ef4444', bg: '#fef2f2', icon: '✕' },
            { list: castEntries.filter(c => c.status === null),      label: '未回答',   color: '#94a3b8', bg: '#f8f9fd', icon: '?' },
          ].map(({ list, label, color, bg, icon }) => list.length > 0 && (
            <div key={label} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color, marginBottom: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontWeight: 900 }}>{icon}</span>
                {label} {list.length}名
              </div>
              {list.map(c => (
                <CastRow key={c.castId} name={c.name} comment={c.comment} bg={bg} />
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

// ── メインコンポーネント ─────────────────────────────────────────────────────
const ScheduleManagerInner = ({ eventId }) => {
  const { casts } = useCasts(eventId);
  const { schedule, loading, ensureToken, toggleDate, addDates, cleanupOldAttendance, setSessions } = useSchedule(eventId);
  const [selectedDate, setSelectedDate] = useState(null);
  const [copyMsg, setCopyMsg] = useState('');
  const [sessionsInput, setSessionsInput] = useState('');

  const now = new Date();
  const months = [
    { year: now.getFullYear(), month: now.getMonth() },
    {
      year: now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear(),
      month: (now.getMonth() + 1) % 12,
    },
  ];

  useEffect(() => {
    if (!loading && schedule.attendance) {
      cleanupOldAttendance(schedule.attendance);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleCopyLink = async () => {
    try {
      const token = await ensureToken();
      const url   = `${window.location.origin}/schedule/${eventId}/${token}`;
      try {
        await navigator.clipboard.writeText(url);
        setCopyMsg('コピーしました！');
      } catch {
        prompt('このURLをコピーしてください:', url);
      }
    } catch {
      setCopyMsg('エラーが発生しました');
    }
    setTimeout(() => setCopyMsg(''), 2500);
  };

  const handleApplyRecurrence = async (rule) => {
    const datesToAdd = [];
    months.forEach(({ year, month }) => {
      datesToAdd.push(...generateRecurringDates(rule, year, month));
    });
    if (datesToAdd.length > 0) await addDates(datesToAdd);
  };

  // sessions input を schedule.sessions に同期（初回のみ）
  useEffect(() => {
    setSessionsInput(schedule.sessions != null ? String(schedule.sessions) : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>読み込み中...</div>
  );

  const attendance        = schedule.attendance        || {};
  const dates             = schedule.dates             || {};
  const sessions          = schedule.sessions          ?? null;
  const session_attendance = schedule.session_attendance || {};
  const cast_comments     = schedule.cast_comments     || {};

  const shareUrl = schedule.token
    ? `${window.location.origin}/schedule/${eventId}/${schedule.token}`
    : null;

  return (
    <div style={{ padding: 24, background: '#f8fafc', minHeight: '100%', boxSizing: 'border-box' }}>

      {/* ── 共有リンク ── */}
      <div style={{
        background: '#fff', borderRadius: 16, padding: '16px 20px',
        marginBottom: 16, border: '1px solid #e8edf5',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 3 }}>
            キャスト向け出欠入力リンク
          </div>
          <div style={{ fontSize: 13, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {shareUrl || 'コピーボタンを押すとリンクが発行されます'}
          </div>
        </div>
        <button
          onClick={handleCopyLink}
          style={{
            padding: '10px 20px', borderRadius: 10, border: 'none',
            background: copyMsg === 'コピーしました！' ? '#10b981' : copyMsg ? '#ef4444' : '#6c63ff',
            color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer',
            transition: 'background 0.2s', whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          {copyMsg || '🔗 リンクをコピー'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>

        {/* ── 左: カレンダー + 繰り返し ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* カレンダー */}
          <div style={{
            background: '#fff', borderRadius: 16, padding: '20px 24px',
            border: '1px solid #e8edf5', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              {months.map(({ year, month }) => (
                <CalendarMonth
                  key={`${year}-${month}`}
                  year={year}
                  month={month}
                  dates={dates}
                  attendance={attendance}
                  casts={casts}
                  onToggleDate={toggleDate}
                  onSelectDate={setSelectedDate}
                  selectedDate={selectedDate}
                  sessions={sessions}
                  session_attendance={session_attendance}
                />
              ))}
            </div>

            {/* 凡例 + 操作ヒント（1か所のみ） */}
            <div style={{
              marginTop: 16, paddingTop: 14, borderTop: '1px solid #f1f5f9',
              display: 'flex', gap: 20, fontSize: 11, color: '#94a3b8',
              flexWrap: 'wrap', alignItems: 'center',
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 14, height: 14, borderRadius: 4, background: '#6c63ff', display: 'inline-block' }} />
                開催日
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontSize: 10, color: '#10b981', fontWeight: 800 }}>+N</span>
                出勤予定人数
              </span>
              <span style={{ marginLeft: 'auto', fontStyle: 'italic' }}>
                クリック: 出勤状況を確認　／　ダブルクリック: 開催日のON/OFF
              </span>
            </div>
          </div>

          {/* 繰り返し設定 */}
          <div style={{
            background: '#fff', borderRadius: 16, padding: '20px 24px',
            border: '1px solid #e8edf5', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>
              繰り返し開催日の一括設定
            </div>
            <RecurrencePanel onApply={handleApplyRecurrence} />
          </div>

          {/* 部制設定 */}
          <div style={{
            background: '#fff', borderRadius: 16, padding: '20px 24px',
            border: '1px solid #e8edf5', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>部制設定</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>同じ日に複数回開催する場合に設定します。キャストの出勤管理が部単位になります。</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="number" min="2" placeholder="例：3"
                value={sessionsInput}
                onChange={e => setSessionsInput(e.target.value)}
                onKeyDown={e => ['e','E','+','-','.'].includes(e.key) && e.preventDefault()}
                style={{ width: 80, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 14, fontWeight: 700, outline: 'none', textAlign: 'center' }}
              />
              <span style={{ fontSize: 13, color: '#475569' }}>部制</span>
              <button
                onClick={() => setSessions(sessionsInput)}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#6c63ff', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
              >保存</button>
              {sessions != null && (
                <button
                  onClick={() => { setSessions(null); setSessionsInput(''); }}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#94a3b8', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                >部制なしに戻す</button>
              )}
            </div>
            {sessions != null && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#6c63ff', fontWeight: 600 }}>現在: 第{sessions}部制</div>
            )}
          </div>
        </div>

        {/* ── 右: 日付詳細 ── */}
        <div style={{
          background: '#fff', borderRadius: 16, padding: '20px',
          border: '1px solid #e8edf5', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          position: 'sticky', top: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 14 }}>
            出勤状況
          </div>
          <DayDetail
            dateStr={selectedDate}
            dates={dates}
            attendance={attendance}
            casts={casts}
            sessions={sessions}
            session_attendance={session_attendance}
            cast_comments={cast_comments}
          />
        </div>
      </div>
    </div>
  );
};

export default ScheduleManagerInner;
